// src/components/practice/tasks/ClozeClickToFill.jsx
import React, { useState } from 'react';
import { marked } from 'marked';
import { X } from 'lucide-react';

const ClozeClickToFill = ({
  partData,
  questions,
  examples,
  partAnswers,
  handleSingleAnswer,
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet
}) => {
    const { content } = partData;
    const mainText = content?.text || '';
    const wordBank = content?.wordBank || [];
    const questionMap = questions.reduce((acc, q) => { acc[q.blankNumber] = q.questionId; return acc; }, {});

    const [activeBlank, setActiveBlank] = useState(null);

    // Add example answers to the list of used words
    const exampleAnswers = examples.map(ex => ex.correctAnswer);
    const usedWords = [...Object.values(partAnswers), ...exampleAnswers];

    const handleBlankClick = (questionId) => {
        setActiveBlank(questionId === activeBlank ? null : questionId);
    };

    const handleWordClick = (word) => {
        if (activeBlank) {
            handleSingleAnswer(activeBlank, word);
            setActiveBlank(null);
        }
    };

    // Updated regex to also catch example gaps like (0)
    const placeholderRegex = /(_{5}\s\(\d+\)|\(\d+\))/g;

    // --- LEFT COLUMN CONTENT (Reading) ---
    const leftContent = (
      <div className="space-y-6">


        {/* Main Text with Blanks */}
        <div className="border-2 border-gray-900 bg-white p-6 rounded-none">
          <div className="prose prose-sm max-w-none text-gray-900 leading-relaxed whitespace-pre-wrap">
            {mainText.split(placeholderRegex).map((part, index) => {
                // Check if this part is a placeholder
                if (placeholderRegex.test(part)) {
                    const blankNumber = part.match(/\d+/)[0];
                    const questionId = questionMap[blankNumber];

                    // Check if this is a real question or an example
                    if (questionId) {
                      // It's a real question, render the button
                      const currentAnswer = partAnswers[questionId];
                      const isActive = activeBlank === questionId;
                      return (
                          <button
                              key={`blank-${index}`}
                              onClick={() => handleBlankClick(questionId)}
                              className={`
                                cursor-pointer inline-block mx-1 p-1 border-b-2 font-semibold text-center min-w-[100px] rounded-none
                                ${isActive 
                                  ? 'border-gray-900 bg-yellow-100' // Active blank
                                  : currentAnswer 
                                    ? 'text-blue-700 border-solid border-gray-900 bg-gray-50' // Filled blank
                                    : 'text-gray-500 border-dashed border-gray-400 hover:border-gray-900' // Empty blank
                                }`}
                          >
                              {currentAnswer || `(${blankNumber})`}
                          </button>
                      );
                    } else {
                      // It's an example, render just the number in a box
                      return (
                        <span
                          key={`blank-${index}`}
                          className="inline-block mx-1 p-1 border-2 border-gray-900 font-semibold text-gray-900 bg-gray-100 rounded-none min-w-[60px] text-center"
                        >
                          ({blankNumber})
                        </span>
                      );
                    }
                }
                // Use marked.parseInline to handle bold/italic in text parts
                return <span key={`text-${index}`} dangerouslySetInnerHTML={{ __html: marked.parseInline(part) }} />;
            })}
          </div>
        </div>
      </div>
    );

    // --- RIGHT COLUMN CONTENT (Answering) ---
    const rightContent = (
      <div className="space-y-4">
        {/* Answer Sheet Header */}
        <div className="border-2 border-gray-900 bg-white p-3 rounded-none mb-4">
          <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
            Antwortbogen
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            Wählen Sie ein Wort:
          </p>
        </div>

        {/* --- Examples List --- */}
        {examples.map(ex => {
          const correctAnswer = Array.isArray(ex.correctAnswer) ? ex.correctAnswer[0] : ex.correctAnswer;

          return (
            <div
              key={ex.questionId}
              className="border border-gray-900 bg-yellow-50 p-4 rounded-none opacity-90 mb-4"
            >
              {/* Example Label */}
              <div className="flex items-start justify-between mb-2">
                <label className="flex-1 font-bold text-sm">
                  {ex.blankNumber}.
                </label>
              </div>

              {/* Disabled Text Input showing the answer */}
              <input
                type="text"
                value={correctAnswer || ''}
                disabled
                className="w-full px-3 py-2 border-2 border-gray-900 rounded-none text-sm bg-gray-100 cursor-not-allowed font-semibold"
              />
            </div>
          );
        })}

        {/* Word Bank */}
        <div className="border-2 border-gray-900 bg-white p-4 rounded-none">
          <div className="flex flex-wrap gap-2">
              {wordBank.map(word => {
                  // This 'isUsed' check now includes example answers
                  const isUsed = usedWords.includes(word);
                  return (
                      <button
                          key={word}
                          onClick={() => handleWordClick(word)}
                          disabled={isUsed || !activeBlank}
                          className={`
                            px-3 py-2 rounded-none font-semibold transition-all text-sm border-2
                            ${isUsed
                              ? 'bg-gray-200 border-gray-300 text-gray-400 line-through cursor-not-allowed' // Used
                              : !activeBlank 
                                ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed' // Disabled (no blank selected)
                                : 'bg-white border-gray-400 text-gray-900 hover:border-gray-900 hover:shadow-md hover:-translate-y-0.5' // Default
                            }`}
                      >
                          {word}
                      </button>
                  );
              })}
          </div>
        </div>
      </div>
    );

    const mobileOverlay = (isMobileAnswerSheetOpen && onCloseMobileAnswerSheet) ? (
      <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCloseMobileAnswerSheet} aria-hidden />
        <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
          <div className="flex items-center justify-between flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
            <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
            <button type="button" onClick={onCloseMobileAnswerSheet} className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center" aria-label="Schließen">
              <X className="w-6 h-6 text-gray-700" />
            </button>
          </div>
          <div className="overflow-y-auto overscroll-contain p-4 pb-safe">{rightContent}</div>
        </div>
      </div>
    ) : null;

    return (
      <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 lg:gap-8">

          {/* Left Column (Text) */}
          <div className="lg:col-span-3 min-w-0">
            {leftContent}
          </div>

          {/* Right Column (Answer Sheet) - hidden on mobile */}
          <div className="hidden lg:block lg:col-span-2 mt-6 lg:mt-0">
            <div className="lg:sticky lg:top-6">
              {rightContent}
            </div>
          </div>

        </div>
        {mobileOverlay}
      </div>
    );
};

export default ClozeClickToFill;